/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { FunctionComponent, VNode } from 'preact';
import { HTMLAttributes, useCallback, JSX, useMemo } from 'preact/compat';
import { classes } from '@adobe-commerce/elsie/lib';
import '@adobe-commerce/elsie/components/ImageSwatch/ImageSwatch.css';
import { Image, ImageProps } from '@adobe-commerce/elsie/components/Image';
import { useText } from '@adobe-commerce/elsie/i18n';

export interface ImageNodeRenderProps extends ImageProps {
  imageSwatchContext: {
    disabled?: boolean;
    outOfStock?: boolean;
    multi?: boolean;
    selected?: boolean;
    value?: string;
    label?: string;
    groupAriaLabel?: string;
    name?: string;
    id?: string;
  };
}

export interface ImageSwatchProps
  extends Omit<HTMLAttributes<HTMLInputElement>, 'label'> {
  name?: string;
  label?: string;
  groupAriaLabel?: string;
  id?: string;
  src: string;
  alt?: string;
  value?: string;
  disabled?: boolean;
  selected?: boolean;
  outOfStock?: boolean;
  multi?: boolean;
  imageNode?: VNode | ((props: ImageNodeRenderProps) => JSX.Element);
  onValue?: (value: any) => void;
  onUpdateError?: (error: Error) => void;
}

export const ImageSwatch: FunctionComponent<ImageSwatchProps> = ({
  className,
  name,
  value,
  id,
  label,
  groupAriaLabel,
  src,
  alt,
  disabled = false,
  selected = false,
  outOfStock = false,
  multi = false,
  imageNode,
  onValue,
  onUpdateError,
  ...props
}) => {
  const outOfStockLabel = useText('Dropin.Swatches.outOfStock.label').label;
  const selectedLabel = useText('Dropin.Swatches.selected.label').label;
  const swatchLabel = useText('Dropin.Swatches.swatch.label').label;

  const onValueHandler = useCallback(
    async (newValue: any) => {
      if (onValue) {
        try {
          await onValue(newValue);
        } catch (e) {
          if (onUpdateError) {
            onUpdateError(e as Error);
          }
        }
      }
    },
    [onValue, onUpdateError]
  );

  const handleOnValue = (e: Event) => {
    const input = e.target as HTMLInputElement;

    onValueHandler(input.value);
  };

  const handleAriaLabel = () => {
    if (outOfStock) return `${groupAriaLabel}: ${label} ${outOfStockLabel}`;
    if (selected) return `${groupAriaLabel}: ${label} ${selectedLabel}`;
    return `${groupAriaLabel}: ${label} ${swatchLabel}`;
  };

  const imageProps: ImageProps = useMemo(() => {
    return {
      src,
      alt,
      loading: 'lazy',
      params: { width: 100, fit: 'bounds', crop: true },
      onError: (e: any) => (e.target.style.display = 'none'),
    };
  }, [src, alt]);

  return (
    <label className={classes(['dropin-image-swatch__container', className])}>
      <input
        type={multi ? 'checkbox' : 'radio'}
        name={name}
        id={id}
        value={value}
        aria-label={handleAriaLabel()}
        checked={selected}
        disabled={disabled}
        onChange={handleOnValue}
        {...props}
        className={classes([
          'dropin-image-swatch',
          ['dropin-image-swatch--selected', selected],
          ['dropin-image-swatch--disabled', disabled],
          className,
        ])}
      />

      <span
        className={classes([
          'dropin-image-swatch__span',
          ['dropin-image-swatch__span--out-of-stock', outOfStock],
          className,
        ])}
      >
        {typeof imageNode === 'function' ? (
          imageNode({
            ...imageProps,
            imageSwatchContext: {
              disabled,
              outOfStock,
              selected,
              value,
              label,
              groupAriaLabel,
              name,
              id,
            },
          })
        ) : imageNode || (
          <Image
            {...imageProps}
            className={classes(['dropin-image-swatch__content'])}
          />
        )}
      </span>
    </label>
  );
};
